import { Component, ElementRef, computed, inject, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TextFieldModule } from '@angular/cdk/text-field';
import { MatTooltip } from '@angular/material/tooltip';
import { ButtonDirective } from '@innersource/common-ui-components';
import { ChatAttachmentTileComponent } from '../chat-attachment-tile/chat-attachment-tile.component';
import { ChatAttachmentsService } from '../chat-attachments.service';
import { ChatConversationService } from '../chat-conversation.service';

/** Accepts a chat prompt and hosts the composer controls. */
@Component({
  selector: 'app-chat-input',
  imports: [FormsModule, TextFieldModule, MatTooltip, ButtonDirective, ChatAttachmentTileComponent],
  templateUrl: './chat-input.component.html',
  standalone: true,
  styleUrl: './chat-input.component.scss',
})
export class ChatInputComponent {
  /** Outputs. */
  readonly send = output<string>();
  readonly focusChange = output<boolean>();

  /** Services. */
  readonly conversation = inject(ChatConversationService);
  readonly attachments = inject(ChatAttachmentsService);

  /** State. */
  readonly messageText = signal('');
  private readonly messageInput =
    viewChild.required<ElementRef<HTMLTextAreaElement>>('messageInput');
  private readonly fileInput = viewChild.required<ElementRef<HTMLInputElement>>('fileInput');

  /** Blocks file selection while the chat is being prepared or files are uploading. */
  readonly fileDisabled = computed(
    () =>
      this.conversation.loading() ||
      this.conversation.creating() ||
      !this.conversation.selectedExpert() ||
      this.attachments.uploading()
  );

  /** Handles enter events. */
  onEnter(event: Event): void {
    event.preventDefault();
    this.emitSend();
  }

  /** Sends the message and clears the input field. */
  emitSend(): void {
    if (
      this.conversation.canSend() &&
      !this.attachments.uploading() &&
      (this.messageText().trim() || this.attachments.pending().length)
    ) {
      this.send.emit(this.messageText());
      this.messageText.set('');
      this.focus();
    }
  }

  /** Opens the native picker immediately from the paperclip control. */
  chooseFiles(): void {
    this.fileInput().nativeElement.click();
  }

  /** Uploads picked files and allows choosing the same file again. */
  onFilesSelected(event: Event): void {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const files = Array.from(input.files ?? []);
    input.value = '';
    if (files.length) void this.attachments.addFiles(files);
  }

  /** Focuses the message field when a new chat is requested. */
  focus(): void {
    this.messageInput().nativeElement.focus();
  }
}
